import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { HeroEditor, AboutEditor, ResearchEditor, FooterEditor, NavbarEditor, ContactEditor, HomepageSectionsEditor, SeoEditor, DirectorEditor, AboutFullEditor } from "@/components/admin/SectionEditors";
import MediaManager from "@/components/MediaManager";
import { MediaPicker } from "@/components/MediaManager";
import TipTapEditor from "@/components/TipTapEditor";
import {
  getProjects, createProject, updateProject, deleteProject,
  getTeam, createTeamMember, updateTeamMember, deleteTeamMember,
  getPartners, createPartner, updatePartner, deletePartner,
  getNews, createNews, updateNews, deleteNews,
  getBlogPosts, createBlogPost, updateBlogPost, deleteBlogPost,
  getContacts, deleteContact,
  getPublications, createPublication, updatePublication, deletePublication,
  getAlbums, createAlbum, updateAlbum, deleteAlbum, getGalleryItems, createGalleryItem, deleteGalleryItem,
  getStats, updateStats,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  FlaskConical, Users, Handshake, Newspaper, BookOpen, MessageSquare,
  BarChart3, Plus, Pencil, Trash2, Image, Eye, EyeOff, Loader2,
  Globe, FileText, TrendingUp, GalleryVerticalEnd, Layout, Search as SearchIcon
} from "lucide-react";

// ===================== GENERIC CRUD PANEL =====================
function CrudPanel({ title, icon: Icon, items, fields, onSave, onDelete, onRefresh, loading }) {
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerField, setPickerField] = useState(null);

  const openCreate = () => {
    setEditing(null);
    const emptyForm = {};
    fields.forEach((f) => { emptyForm[f.key] = f.defaultValue || ""; });
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    const editForm = {};
    fields.forEach((f) => { editForm[f.key] = item[f.key] ?? f.defaultValue ?? ""; });
    setForm(editForm);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      await onSave(form, editing);
      setDialogOpen(false);
      onRefresh();
    } catch (err) {
      alert("Erreur: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    await onDelete(id);
    onRefresh();
  };

  const openMediaPicker = (fieldKey) => {
    setPickerField(fieldKey);
    setPickerOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-[#1670B9]" />
          <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800">{title}</h2>
          <span className="text-sm text-slate-400">({items.length})</span>
        </div>
        <Button onClick={openCreate} className="bg-[#1670B9] hover:bg-[#125c9e] rounded-xl" data-testid={`add-${title.toLowerCase()}-btn`}>
          <Plus className="w-4 h-4 mr-2" /> Ajouter
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-['Quicksand']">{editing ? "Modifier" : "Ajouter"} - {title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {fields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                {f.type === "richtext" ? (
                  <TipTapEditor content={form[f.key] || ""} onChange={(v) => setForm({...form, [f.key]: v})} />
                ) : f.type === "textarea" ? (
                  <Textarea value={form[f.key] || ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})} rows={4} className="rounded-xl" data-testid={`form-${f.key}`} />
                ) : f.type === "select" ? (
                  <select value={form[f.key] || ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid={`form-${f.key}`}>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === "image" ? (
                  <div className="flex gap-2">
                    <Input value={form[f.key] || ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})} className="rounded-xl flex-1" placeholder="URL de l'image" data-testid={`form-${f.key}`} />
                    <Button type="button" variant="outline" onClick={() => openMediaPicker(f.key)} className="rounded-xl px-3">
                      <Image className="w-4 h-4" />
                    </Button>
                    {form[f.key] && <img src={form[f.key]} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                  </div>
                ) : f.type === "number" ? (
                  <Input type="number" value={form[f.key] ?? ""} onChange={(e) => setForm({...form, [f.key]: parseInt(e.target.value) || 0})} className="rounded-xl" data-testid={`form-${f.key}`} />
                ) : (
                  <Input type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({...form, [f.key]: e.target.value})} className="rounded-xl" data-testid={`form-${f.key}`} />
                )}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} className="bg-[#1670B9] hover:bg-[#125c9e] rounded-xl flex-1" data-testid="form-save-btn">
                {editing ? "Mettre à jour" : "Créer"}
              </Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => {
        if (pickerField) setForm({...form, [pickerField]: url});
        setPickerOpen(false);
      }} />

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1670B9]" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-100">
          <Icon className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p>Aucun élément. Cliquez sur "Ajouter" pour commencer.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between bg-white rounded-xl p-4 border border-slate-100 hover:shadow-sm transition-shadow" data-testid={`crud-item-${item.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {(item.image_url || item.photo_url || item.logo_url) && (
                  <img src={item.image_url || item.photo_url || item.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{item.title || item.name}</p>
                  <p className="text-xs text-slate-400 truncate">{item.summary || item.role || item.website || item.category || ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 ml-4">
                {item.status && (
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${item.status === 'published' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                    {item.status === 'published' ? 'Publié' : 'Brouillon'}
                  </span>
                )}
                <button onClick={() => openEdit(item)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-[#1670B9] transition-colors" data-testid={`edit-${item.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-${item.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== STATS EDITOR =====================
function StatsEditor() {
  const [stats, setStats] = useState({ projects_count: 0, publications_count: 0, researchers_count: 0, partners_count: 0 });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    getStats().then(r => setStats(r.data)).catch(() => {});
  }, []); // getStats is a stable module-level import
  const handleSave = async () => {
    await updateStats(stats);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };
  return (
    <div>
      <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-[#1670B9]" /> Statistiques du site
      </h2>
      <div className="bg-white rounded-xl p-6 border border-slate-100 grid grid-cols-2 gap-5">
        {[
          { key: "projects_count", label: "Projets", icon: "flask" },
          { key: "publications_count", label: "Publications", icon: "book" },
          { key: "researchers_count", label: "Chercheurs", icon: "users" },
          { key: "partners_count", label: "Partenaires", icon: "handshake" },
        ].map(s => (
          <div key={s.key}>
            <label className="block text-sm font-medium text-slate-700 mb-1">{s.label}</label>
            <Input type="number" value={stats[s.key]} onChange={e => setStats({...stats, [s.key]: parseInt(e.target.value)||0})} className="rounded-xl" data-testid={`stat-${s.key}`} />
          </div>
        ))}
        <div className="col-span-2">
          <Button onClick={handleSave} className="bg-[#F49C03] hover:bg-[#e08e00] rounded-xl" data-testid="save-stats-btn">
            {saved ? "Sauvegardé !" : "Enregistrer les statistiques"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ===================== MESSAGES =====================
function MessagesPanel() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(() => {
    setLoading(true);
    getContacts().then(r => setMessages(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  return (
    <div>
      <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-[#1670B9]" /> Messages ({messages.length})
      </h2>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1670B9]" /></div> :
      messages.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-100">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p>Aucun message.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <div key={msg.id} className="bg-white rounded-xl p-5 border border-slate-100" data-testid={`message-${msg.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div><p className="font-medium text-slate-800">{msg.name}</p><p className="text-sm text-[#1670B9]">{msg.email}</p></div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{msg.created_at && new Date(msg.created_at).toLocaleDateString("fr-FR")}</span>
                  <button onClick={async () => { await deleteContact(msg.id); refresh(); }} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-600">{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ===================== OVERVIEW =====================
function OverviewPanel({ counts }) {
  const cards = [
    { label: "Projets", count: counts.projects, icon: FlaskConical, color: "#1670B9" },
    { label: "Publications", count: counts.publications, icon: FileText, color: "#1670B9" },
    { label: "Membres", count: counts.team, icon: Users, color: "#74CDD9" },
    { label: "Actualités", count: counts.news, icon: Newspaper, color: "#F49C03" },
    { label: "Blog", count: counts.blog, icon: BookOpen, color: "#1670B9" },
    { label: "Albums", count: counts.albums, icon: GalleryVerticalEnd, color: "#74CDD9" },
    { label: "Partenaires", count: counts.partners, icon: Handshake, color: "#74CDD9" },
    { label: "Messages", count: counts.messages, icon: MessageSquare, color: "#F49C03" },
  ];
  return (
    <div>
      <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-6">Tableau de bord</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-xl p-5 border border-slate-100">
            <c.icon className="w-5 h-5 mb-3" style={{ color: c.color }} />
            <p className="text-2xl font-bold text-slate-800 font-['Quicksand']">{c.count}</p>
            <p className="text-sm text-slate-400">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl p-6 border border-slate-100">
        <h3 className="font-['Quicksand'] text-base font-semibold text-slate-800 mb-3">Actions rapides</h3>
        <div className="flex flex-wrap gap-3">
          <a href="/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1670B9]/10 text-[#1670B9] text-sm font-medium hover:bg-[#1670B9]/20 transition-colors">
            <Globe className="w-4 h-4" /> Voir le site public
          </a>
        </div>
      </div>
    </div>
  );
}

// ===================== MAIN DASHBOARD =====================
export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [partners, setPartners] = useState([]);
  const [news, setNews] = useState([]);
  const [blog, setBlog] = useState([]);
  const [messages, setMessages] = useState([]);
  const [publications, setPublications] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loadingData, setLoadingData] = useState({});

  const load = useCallback((key, fn, setter) => {
    setLoadingData(p => ({...p, [key]: true}));
    fn().then(r => setter(r.data)).catch(() => {}).finally(() => setLoadingData(p => ({...p, [key]: false})));
  }, []);

  useEffect(() => {
    load("projects", getProjects, setProjects);
    load("team", getTeam, setTeam);
    load("partners", getPartners, setPartners);
    load("news", getNews, setNews);
    load("blog", getBlogPosts, setBlog);
    load("messages", getContacts, setMessages);
    load("publications", getPublications, setPublications);
    load("albums", getAlbums, setAlbums);
  }, [load]);

  const renderContent = () => {
    switch (activeTab) {
      case "overview":
        return <OverviewPanel counts={{ projects: projects.length, team: team.length, partners: partners.length, news: news.length, blog: blog.length, messages: messages.length, publications: publications.length, albums: albums.length }} />;

      case "site-config":
        return (
          <div>
            <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#1670B9]" /> Sections du site
            </h2>
            <NavbarEditor />
            <HeroEditor />
            <AboutEditor />
            <ResearchEditor />
            <ContactEditor />
            <FooterEditor />
          </div>
        );

      case "projects":
        return <CrudPanel title="Projets" icon={FlaskConical} items={projects} loading={loadingData.projects}
          fields={[
            { key: "title", label: "Titre", type: "text" },
            { key: "summary", label: "Résumé", type: "textarea" },
            { key: "content", label: "Contenu détaillé", type: "richtext" },
            { key: "category", label: "Catégorie", type: "select", options: ["Recherche", "Innovation", "Collaboration"], defaultValue: "Recherche" },
            { key: "image_url", label: "Image", type: "image" },
            { key: "tags", label: "Tags (séparés par virgules)", type: "text" },
            { key: "status", label: "Statut", type: "select", options: ["published", "draft"], defaultValue: "published" },
          ]}
          onSave={async (form, editId) => {
            const data = {...form};
            if (typeof data.tags === "string") data.tags = data.tags.split(",").map(t => t.trim()).filter(Boolean);
            return editId ? await updateProject(editId, data) : await createProject(data);
          }}
          onDelete={deleteProject}
          onRefresh={() => load("projects", getProjects, setProjects)}
        />;

      case "team":
        return <CrudPanel title="Équipe" icon={Users} items={team} loading={loadingData.team}
          fields={[
            { key: "name", label: "Nom complet", type: "text" },
            { key: "role", label: "Rôle / Fonction", type: "text" },
            { key: "email", label: "Email", type: "email" },
            { key: "photo_url", label: "Photo", type: "image" },
            { key: "bio", label: "Biographie", type: "richtext" },
            { key: "speciality", label: "Spécialité", type: "text" },
            { key: "member_type", label: "Type", type: "select", options: ["permanent", "associé"], defaultValue: "permanent" },
            { key: "order", label: "Ordre d'affichage", type: "number", defaultValue: 0 },
          ]}
          onSave={async (form, editId) => editId ? await updateTeamMember(editId, form) : await createTeamMember(form)}
          onDelete={deleteTeamMember}
          onRefresh={() => load("team", getTeam, setTeam)}
        />;

      case "partners":
        return <CrudPanel title="Partenaires" icon={Handshake} items={partners} loading={loadingData.partners}
          fields={[
            { key: "name", label: "Nom", type: "text" },
            { key: "logo_url", label: "Logo", type: "image" },
            { key: "website", label: "Site web", type: "text" },
            { key: "category", label: "Catégorie", type: "text" },
            { key: "order", label: "Ordre", type: "number", defaultValue: 0 },
          ]}
          onSave={async (form, editId) => editId ? await updatePartner(editId, form) : await createPartner(form)}
          onDelete={deletePartner}
          onRefresh={() => load("partners", getPartners, setPartners)}
        />;

      case "news":
        return <CrudPanel title="Actualités" icon={Newspaper} items={news} loading={loadingData.news}
          fields={[
            { key: "title", label: "Titre", type: "text" },
            { key: "summary", label: "Résumé", type: "textarea" },
            { key: "content", label: "Contenu", type: "richtext" },
            { key: "image_url", label: "Image", type: "image" },
            { key: "tags", label: "Tags (virgules)", type: "text" },
            { key: "status", label: "Statut", type: "select", options: ["published", "draft"], defaultValue: "published" },
          ]}
          onSave={async (form, editId) => {
            const data = {...form};
            if (typeof data.tags === "string") data.tags = data.tags.split(",").map(t => t.trim()).filter(Boolean);
            return editId ? await updateNews(editId, data) : await createNews(data);
          }}
          onDelete={deleteNews}
          onRefresh={() => load("news", getNews, setNews)}
        />;

      case "blog":
        return <CrudPanel title="Blog" icon={BookOpen} items={blog} loading={loadingData.blog}
          fields={[
            { key: "title", label: "Titre", type: "text" },
            { key: "summary", label: "Résumé", type: "textarea" },
            { key: "content", label: "Contenu de l'article", type: "richtext" },
            { key: "image_url", label: "Image couverture", type: "image" },
            { key: "tags", label: "Tags (virgules)", type: "text" },
            { key: "status", label: "Statut", type: "select", options: ["published", "draft"], defaultValue: "published" },
          ]}
          onSave={async (form, editId) => {
            const data = {...form};
            if (typeof data.tags === "string") data.tags = data.tags.split(",").map(t => t.trim()).filter(Boolean);
            return editId ? await updateBlogPost(editId, data) : await createBlogPost(data);
          }}
          onDelete={deleteBlogPost}
          onRefresh={() => load("blog", getBlogPosts, setBlog)}
        />;

      case "media":
        return <MediaManager />;

      case "messages":
        return <MessagesPanel />;

      case "stats":
        return <StatsEditor />;

      case "homepage":
        return (
          <div>
            <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <Layout className="w-5 h-5 text-[#1670B9]" /> Page d'accueil
            </h2>
            <HomepageSectionsEditor />
          </div>
        );

      case "director":
        return (
          <div>
            <DirectorEditor />
            <AboutFullEditor />
          </div>
        );

      case "publications":
        return <CrudPanel title="Publications" icon={FileText} items={publications} loading={loadingData.publications}
          fields={[
            { key: "title", label: "Titre", type: "text" },
            { key: "authors", label: "Auteurs", type: "text" },
            { key: "summary", label: "Résumé", type: "textarea" },
            { key: "year", label: "Année", type: "number", defaultValue: new Date().getFullYear() },
            { key: "doi", label: "DOI", type: "text" },
            { key: "pdf_url", label: "Lien PDF", type: "image" },
            { key: "image_url", label: "Image couverture", type: "image" },
            { key: "tags", label: "Tags (virgules)", type: "text" },
            { key: "status", label: "Statut", type: "select", options: ["published", "draft"], defaultValue: "published" },
          ]}
          onSave={async (form, editId) => {
            const data = {...form};
            if (typeof data.tags === "string") data.tags = data.tags.split(",").map(t => t.trim()).filter(Boolean);
            if (typeof data.year === "string") data.year = parseInt(data.year) || 2025;
            return editId ? await updatePublication(editId, data) : await createPublication(data);
          }}
          onDelete={deletePublication}
          onRefresh={() => load("publications", getPublications, setPublications)}
        />;

      case "gallery":
        return <GalleryAdmin albums={albums} loadingAlbums={loadingData.albums} onRefresh={() => load("albums", getAlbums, setAlbums)} />;

      case "seo":
        return (
          <div>
            <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <SearchIcon className="w-5 h-5 text-[#1670B9]" /> SEO & Métadonnées
            </h2>
            <SeoEditor />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AdminLayout activeTab={activeTab} onTabChange={setActiveTab}>
      {renderContent()}
    </AdminLayout>
  );
}

// ===================== GALLERY ADMIN =====================
function GalleryAdmin({ albums, loadingAlbums, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", cover_url: "", category: "Photos", order: 0 });
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [items, setItems] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [itemPickerOpen, setItemPickerOpen] = useState(false);

  const refreshItems = useCallback(() => {
    if (selectedAlbum) getGalleryItems(selectedAlbum.id).then(r => setItems(r.data)).catch(() => {});
  }, [selectedAlbum]);

  useEffect(() => { refreshItems(); }, [refreshItems]);

  const openCreate = () => { setEditing(null); setForm({ title: "", description: "", cover_url: "", category: "Photos", order: 0 }); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a.id); setForm({ title: a.title, description: a.description || "", cover_url: a.cover_url || "", category: a.category || "Photos", order: a.order || 0 }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) await updateAlbum(editing, form);
      else await createAlbum(form);
      setDialogOpen(false);
      onRefresh();
    } catch (err) { alert("Erreur: " + err.message); }
  };

  const handleAddItem = (url) => {
    if (selectedAlbum) {
      createGalleryItem({ album_id: selectedAlbum.id, url, media_type: url.match(/\.(mp4|webm)/i) ? "video" : "image", title: "", order: items.length })
        .then(() => refreshItems()).catch(() => {});
    }
  };

  if (selectedAlbum) {
    return (
      <div>
        <button onClick={() => setSelectedAlbum(null)} className="inline-flex items-center gap-2 text-sm text-[#1670B9] font-medium mb-4 hover:underline" data-testid="back-albums-admin">
          ← Retour aux albums
        </button>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800">{selectedAlbum.title}</h2>
          <Button onClick={() => setItemPickerOpen(true)} className="bg-[#1670B9] hover:bg-[#125c9e] rounded-xl" data-testid="add-gallery-item-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter média
          </Button>
        </div>
        <MediaPicker open={itemPickerOpen} onClose={() => setItemPickerOpen(false)} onSelect={(url) => { handleAddItem(url); setItemPickerOpen(false); }} />
        {items.length === 0 ? (
          <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-100">
            <Image className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p>Album vide. Ajoutez des médias.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {items.map(item => (
              <div key={item.id} className="group relative rounded-xl overflow-hidden bg-white border border-slate-100">
                {item.media_type === "video" ? (
                  <video src={item.url} className="w-full h-28 object-cover" />
                ) : (
                  <img src={item.url} alt="" className="w-full h-28 object-cover" />
                )}
                <button onClick={async () => { await deleteGalleryItem(item.id); refreshItems(); }}
                  className="absolute top-1 right-1 w-5 h-5 rounded bg-red-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Quicksand'] text-xl font-semibold text-slate-800 flex items-center gap-2">
          <GalleryVerticalEnd className="w-5 h-5 text-[#1670B9]" /> Albums ({albums.length})
        </h2>
        <Button onClick={openCreate} className="bg-[#1670B9] hover:bg-[#125c9e] rounded-xl" data-testid="add-album-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvel album
        </Button>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-['Quicksand']">{editing ? "Modifier" : "Créer"} un album</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Titre</label>
              <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-xl" data-testid="album-title-input" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="rounded-xl" /></div>
            <div><label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm">
                {["Photos", "Vidéos", "Terrain", "Conférences", "Laboratoire"].map(c => <option key={c}>{c}</option>)}
              </select></div>
            <div className="flex gap-2">
              <Input value={form.cover_url} onChange={e => setForm({...form, cover_url: e.target.value})} placeholder="URL couverture" className="rounded-xl flex-1" />
              <Button variant="outline" onClick={() => setPickerOpen(true)} className="rounded-xl px-3"><Image className="w-4 h-4" /></Button>
            </div>
            <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={url => { setForm({...form, cover_url: url}); setPickerOpen(false); }} />
            <div className="flex gap-3">
              <Button onClick={handleSave} className="bg-[#1670B9] hover:bg-[#125c9e] rounded-xl flex-1" data-testid="save-album-btn">{editing ? "Mettre à jour" : "Créer"}</Button>
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {albums.length === 0 ? (
        <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-slate-100">
          <GalleryVerticalEnd className="w-10 h-10 mx-auto mb-3 text-slate-300" /><p>Aucun album. Créez votre premier album.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {albums.map(a => (
            <div key={a.id} className="flex items-center justify-between bg-white rounded-xl p-4 border border-slate-100 hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => setSelectedAlbum(a)} data-testid={`album-item-${a.id}`}>
                {a.cover_url ? <img src={a.cover_url} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center"><Image className="w-5 h-5 text-slate-300" /></div>}
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{a.title}</p>
                  <p className="text-xs text-slate-400">{a.category}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 ml-4">
                <button onClick={() => openEdit(a)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-[#1670B9]"><Pencil className="w-4 h-4" /></button>
                <button onClick={async () => { if(window.confirm("Supprimer ?")) { await deleteAlbum(a.id); onRefresh(); } }} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
